import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView, useMotionValue, useTransform, useSpring, useAnimation } from 'framer-motion';
import './Services.css';

// ===== SERVICE DATA =====
const serviceItems = [
  {
    id: 1,
    title: 'Security Guards',
    desc: 'Professionally trained armed & unarmed security guards for round-the-clock protection of your premises.',
    icon: '🛡️',
    gradient: 'from-teal-500/20 via-emerald-500/10 to-transparent',
    borderGlow: 'rgba(13,148,136,0.4)',
    stats: '250+ Guards Deployed',
    statNumber: 250,
    statLabel: '+ Guards',
  },
  {
    id: 2,
    title: 'Residential Security',
    desc: 'Comprehensive security solutions for housing societies with smart entry systems and 24/7 monitoring.',
    icon: '🏘️',
    gradient: 'from-cyan-500/20 via-blue-500/10 to-transparent',
    borderGlow: 'rgba(8,145,178,0.4)',
    stats: '50+ Societies',
    statNumber: 50,
    statLabel: '+ Societies',
  },
  {
    id: 3,
    title: 'Commercial Security',
    desc: 'Advanced security systems for offices, corporate buildings, and commercial complexes.',
    icon: '🏢',
    gradient: 'from-sky-500/20 via-indigo-500/10 to-transparent',
    borderGlow: 'rgba(14,165,233,0.4)',
    stats: '100+ Clients',
    statNumber: 100,
    statLabel: '+ Clients',
  },
  {
    id: 4,
    title: 'Industrial Security',
    desc: 'Specialized security operations for factories, warehouses, and large-scale industrial facilities.',
    icon: '🏭',
    gradient: 'from-violet-500/20 via-purple-500/10 to-transparent',
    borderGlow: 'rgba(139,92,246,0.4)',
    stats: '24/7 Coverage',
    statNumber: 24,
    statLabel: '/7 Coverage',
  },
  {
    id: 5,
    title: 'CCTV & Surveillance',
    desc: 'State-of-the-art CCTV installation with AI-powered monitoring and real-time threat detection.',
    icon: '📹',
    gradient: 'from-rose-500/20 via-pink-500/10 to-transparent',
    borderGlow: 'rgba(244,63,94,0.4)',
    stats: '500+ Cameras',
    statNumber: 500,
    statLabel: '+ Cameras',
  },
  {
    id: 6,
    title: 'Event Security',
    desc: 'Professional crowd management and security planning for events of all scales.',
    icon: '🎯',
    gradient: 'from-amber-500/20 via-orange-500/10 to-transparent',
    borderGlow: 'rgba(245,158,11,0.4)',
    stats: '200+ Events',
    statNumber: 200,
    statLabel: '+ Events',
  },
];

// ===== ANIMATED COUNT-UP HOOK =====
const useCountUp = (target, isInView, duration = 2) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let startTime = null;
    let animationId;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [target, isInView, duration]);

  return count;
};

// ===== ORBITING PARTICLES COMPONENT =====
const OrbitingParticles = ({ borderGlow }) => {
  const particles = Array.from({ length: 4 });
  return (
    <div className="service-card-orbit">
      {particles.map((_, i) => {
        const angle = (i / particles.length) * 360;
        const radius = 35 + Math.random() * 10;
        const rad = (angle * Math.PI) / 180;
        return (
          <motion.div
            key={i}
            className="service-card-orbit-particle"
            style={{
              left: `calc(50% + ${Math.cos(rad) * radius}px - 2px)`,
              top: `calc(50% + ${Math.sin(rad) * radius}px - 2px)`,
              background: borderGlow,
              boxShadow: `0 0 6px ${borderGlow}`,
            }}
            animate={{
              scale: [0.5, 1.2, 0.5],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 2 + Math.random(),
              repeat: Infinity,
              delay: i * 0.5,
              ease: 'easeInOut',
            }}
          />
        );
      })}
    </div>
  );
};

// ===== PARALLAX CARD COMPONENT =====
const ServiceCard = ({ service, index }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: '-50px' });
  const [isHovered, setIsHovered] = useState(false);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useTransform(mouseY, [-0.5, 0.5], [8, -8]);
  const rotateY = useTransform(mouseX, [-0.5, 0.5], [-8, 8]);

  // Spring physics for smoother rotation
  const springRotateX = useSpring(rotateX, { stiffness: 200, damping: 25 });
  const springRotateY = useSpring(rotateY, { stiffness: 200, damping: 25 });

  // Z-translate on hover
  const zValue = useTransform(mouseX, [-0.5, 0.5], [-10, 10]);
  const springZ = useSpring(zValue, { stiffness: 200, damping: 25 });

  const handleMouseMove = useCallback((e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  }, [mouseX, mouseY]);

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0);
    mouseY.set(0);
    setIsHovered(false);
  }, [mouseX, mouseY]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const countedStat = useCountUp(service.statNumber, isInView, 2);

  // Variants for staggered children
  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        delay: index * 0.1,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <motion.div
      ref={cardRef}
      variants={cardVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        perspective: 1000,
        rotateX: isInView ? springRotateX : 0,
        rotateY: isInView ? springRotateY : 0,
        z: isInView ? springZ : 0,
      }}
      whileHover={{ y: -10, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }}
      className="service-card-wrapper"
    >
      <div
        className="service-card"
        style={{ '--border-glow': service.borderGlow }}
      >
        {/* ===== Card Background ===== */}
        <div className={`service-card-bg ${service.gradient}`} />

        {/* ===== Glow Effect ===== */}
        <div
          className="service-card-glow"
          style={{
            background: `radial-gradient(circle at 50% 0%, ${service.borderGlow}, transparent 70%)`,
          }}
        />

        {/* ===== Orbiting Particles (visible on hover) ===== */}
        {isHovered && <OrbitingParticles borderGlow={service.borderGlow} />}

        {/* ===== Icon Container ===== */}
        <motion.div
          className="service-icon-wrap"
          animate={isHovered ? {
            rotate: [0, -8, 8, -4, 0],
          } : {}}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        >
          <motion.div
            className="service-icon"
            style={{ borderColor: isHovered ? service.borderGlow : 'rgba(255,255,255,0.06)' }}
            animate={isHovered ? {
              scale: 1.08,
              borderRadius: '16px',
            } : {
              scale: 1,
              borderRadius: '20px',
            }}
            transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <motion.span
              className="service-icon-emoji"
              animate={isHovered ? {
                y: [0, -5, 0],
              } : {}}
              transition={{ duration: 1.2, repeat: isHovered ? Infinity : 0, ease: 'easeInOut' }}
            >
              {service.icon}
            </motion.span>
          </motion.div>
        </motion.div>

        {/* ===== Content ===== */}
        <motion.h3
          className="service-card-title"
          initial={{ opacity: 0, x: -15 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
        >
          {service.title}
        </motion.h3>

        <motion.p
          className="service-card-desc"
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.3 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
        >
          {service.desc}
        </motion.p>

        {/* ===== Divider ===== */}
        <motion.div
          className="service-card-divider"
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 + index * 0.08 }}
          style={{ transformOrigin: 'left' }}
        />

        {/* ===== Footer ===== */}
        <div className="service-card-footer">
          <motion.span
            className="service-card-stat"
            initial={{ opacity: 0, x: -10 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.5 + index * 0.08, duration: 0.4 }}
          >
            <span className="stat-dot" />
            <span className="stat-number">{countedStat}</span>
            {service.statLabel}
          </motion.span>

          {/* ===== Magnetic CTA Link ===== */}
          <MagneticCTA
            delay={0.55 + index * 0.08}
            isInView={isInView}
            borderGlow={service.borderGlow}
          />
        </div>
      </div>
    </motion.div>
  );
};

// ===== MAGNETIC CTA COMPONENT =====
const MagneticCTA = ({ delay, isInView, borderGlow }) => {
  const ctaRef = useRef(null);
  const [ctaPos, setCtaPos] = useState({ x: 0, y: 0 });

  const handleCtaMouse = useCallback((e) => {
    if (!ctaRef.current) return;
    const rect = ctaRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    setCtaPos({ x: x * 0.2, y: y * 0.15 });
  }, []);

  const handleCtaLeave = useCallback(() => {
    setCtaPos({ x: 0, y: 0 });
  }, []);

  return (
    <motion.a
      ref={ctaRef}
      href="#contact"
      className="service-card-cta"
      initial={{ opacity: 0, x: 10 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay, duration: 0.4 }}
      onMouseMove={handleCtaMouse}
      onMouseLeave={handleCtaLeave}
      style={{
        x: ctaPos.x,
        y: ctaPos.y,
      }}
      whileHover={{ color: '#14b8a6' }}
    >
      Learn More
      <svg className="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </motion.a>
  );
};

// ===== TITLE TEXT REVEAL =====
const TitleReveal = ({ isInView }) => {
  const title = "Comprehensive ";
  const highlight = "Security Solutions";

  return (
    <h2 className="services-title">
      {title.split('').map((char, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 20, rotateX: -40 }}
          animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
          transition={{
            duration: 0.4,
            delay: i * 0.02,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{ display: 'inline-block' }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
      <motion.span
        className="services-title-highlight"
        initial={{ opacity: 0, y: 20, rotateX: -40 }}
        animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
        transition={{
          duration: 0.5,
          delay: 0.3 + title.length * 0.02,
          ease: [0.22, 1, 0.36, 1],
        }}
        style={{ display: 'inline-block' }}
      >
        {' '}{highlight.split('').map((char, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 15, scale: 0.8 }}
            animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{
              duration: 0.35,
              delay: 0.35 + i * 0.025,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{ display: 'inline-block' }}
          >
            {char === ' ' ? '\u00A0' : char}
          </motion.span>
        ))}
      </motion.span>
    </h2>
  );
};

// ===== MAIN SERVICES COMPONENT =====
const ServicesSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const controls = useAnimation();

  // Particle positions — randomized once
  const [particles] = useState(() =>
    Array.from({ length: 8 }).map((_, i) => ({
      left: `${10 + Math.random() * 80}%`,
      top: `${10 + Math.random() * 80}%`,
      width: 2 + Math.random() * 4,
      height: 2 + Math.random() * 4,
      duration: 4 + Math.random() * 5,
      delay: Math.random() * 4,
      xRange: [-20, 20],
      yRange: [-30, 0],
    }))
  );

  return (
    <section id="services" className="services-section" ref={sectionRef}>
      {/* ===== Background Elements ===== */}
      <div className="services-bg-grid" />
      <div className="services-bg-glow-top" />
      <div className="services-bg-glow-bottom" />
      <div className="services-bg-accent" />

      {/* ===== Floating Particles ===== */}
      <div className="services-particles">
        {particles.map((p, i) => (
          <motion.div
            key={i}
            className="services-particle"
            animate={{
              y: [p.yRange[0], p.yRange[1], p.yRange[0]],
              x: [p.xRange[0], p.xRange[1], p.xRange[0]],
              opacity: [0, 0.5, 0],
              scale: [0, 1.2, 0],
            }}
            transition={{
              duration: p.duration,
              repeat: Infinity,
              delay: p.delay,
              ease: 'easeInOut',
            }}
            style={{
              left: p.left,
              top: p.top,
              width: p.width,
              height: p.height,
            }}
          />
        ))}
      </div>

      <div className="services-container">
        {/* ===== HEADER ===== */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="services-header"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.34, 1.56, 0.64, 1] }}
            className="services-badge-wrap"
          >
            <span className="services-badge">
              <motion.span
                className="badge-dot"
                animate={{ scale: [1, 1.6, 1], opacity: [1, 0.4, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              />
              Our Services
            </span>
          </motion.div>

          {/* Animated Title with Character Reveal */}
          <TitleReveal isInView={isInView} />

          <motion.p
            className="services-subtitle"
            initial={{ opacity: 0, y: 15 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            We provide end-to-end security services tailored to protect your
            business, property, and people with professionalism and care.
          </motion.p>
        </motion.div>

        {/* ===== SERVICES GRID ===== */}
        <motion.div
          className="services-grid"
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={{
            visible: { transition: { staggerChildren: 0.08 } },
            hidden: {},
          }}
        >
          {serviceItems.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
            />
          ))}
        </motion.div>

        {/* ===== BOTTOM CTA ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
          className="services-bottom-cta"
        >
          <p className="services-bottom-text">
            Need a custom security plan? We've got you covered.
          </p>
          <motion.a
            href="#contact"
            className="services-bottom-btn"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Free Consultation
            <svg className="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;

